import * as React from "react";
import debounce from "lodash.debounce";
import Box from "@mui/material/Box";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Skeleton from "@mui/material/Skeleton";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import { Grid, InputAdornment } from "@mui/material";
import SearchListGrid from "../searchListGrid/SearchListGrid";
import {
  getExtractorDetailSelector,
  getExtractorListPendingSelector,
  getExtractorListSelector,
} from "../../../store/extractor/selectors";
import {
  fetchExtractorDetailReq,
  fetchExtractorListReq,
} from "../../../store/extractor/actions";
import {
  IDataExtractor,
  IDataExtractorDetails,
  IExtractorListParams,
  ISingleExtractorDetail,
} from "../../../store/extractor/types";
import { HeadCell, Order } from "../tableGrid/types";
import EnhancedTable from "../tableGrid/tableGrid";
import EnhancedTableToolbar from "../tableGrid/tableToolbar";
import EnhancedTableHead from "../tableGrid/tableHead";
import Row from "../tableGrid/tableRow";
import { useNavigate } from "react-router-dom";
import {
  enableWorkflow,
  toggleDataSet,
  disableWorkflow,
  resetWorkflow,
} from "../../../store/dataProvisioningWorkflow/actions";
import { getDataProvisioningWorkflowStateSelector } from "../../../store/dataProvisioningWorkflow/selectors";
import DataProvisioningWorkflow from "../../../components/organisms/dataProvisioningWorkflow/dataProvisioningWorkflow";
import {getProfilePendingSelector, getProfileSelector} from '../../../store/userProfile/selectors';
import { IProfile } from "../../../store/userProfile/types";
import * as actions from "../../../store/userProfile/actions";
import { getProfileDataSelector } from '../../../store/auth/selectors';
import {SEARCH_DEBOUNCE_TIME_MILLIS} from "../../../constants/common";
import { getPrivacyClassificationText, getSecurityClassificationText } from "../../shared/functions";


// settings data

const headCells: HeadCell[] = [
  {
    id: "selected",
    numeric: false,
    disablePadding: true,
    label: "",
    width: "5px",
  },
  {
    id: "treatmentLevel",
    numeric: false,
    disablePadding: true,
    label: "",
    width: "5px",
  },
  {
    id: "accessControl",
    numeric: false,
    disablePadding: true,
    label: "",
    width: "5px",
  },
  {
    id: "description",
    numeric: false,
    disablePadding: true,
    label: "Description",
    width: "250px",
  },
  {
    id: "tableId",
    numeric: false,
    disablePadding: true,
    label: "Name",
    width: "350px",
  },
  {
    id: "schema",
    numeric: false,
    disablePadding: false,
    label: "Schema",
    width: "50px",
  },
  {
    id: "table",
    numeric: false,
    disablePadding: false,
    label: "Table",
    width: "150px",
  },
];

// end

const descendingComparator = (
  a: IDataExtractor,
  b: IDataExtractor,
  orderBy: keyof IDataExtractor
) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (
  order: Order,
  orderBy: keyof IDataExtractor
): ((a: IDataExtractor, b: IDataExtractor) => number) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

export type SetExplorerProps = {
  isAdmin: boolean;
}

const SetExplorer = (props: SetExplorerProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dataRows, setDataRows] = useState<IDataExtractor[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof IDataExtractor>("description");
  const [rowDetails, setRowDetails] = useState<ISingleExtractorDetail[]>([]);
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [schemas, setSchemas] = useState<string[]>([]);
  const [tables, setTables] = useState<string[]>([]);
  const [treatmentLevels, setTreatmentLevels] = useState<string[]>([]);
  const [accessControls, setAccessControls] = useState<string[]>([]);
  const [sourceNames, setSourceNames] = useState<string[]>([]);
  const [selectedExtractor, setSelectedExtractor] = useState(false);
  const userProfile = useSelector(getProfileSelector);
  const userProfilePending = useSelector(getProfilePendingSelector);
  const [mail, setMail] = useState("")
  const [dataStewards, setDataStewards] = useState<string[]>([]);
  const [spokeName, setSpokeName] = useState<string[]>([]);

  //filters
  const [filters, setFilters] = useState<string[]>([]);
  const [openFilters, setOpenFilters] = useState(false);

  const selectable: string = "single";
  const allSelectable: boolean = false;
  const authProfile = useSelector(getProfileDataSelector);


  useEffect(() => {
    if (authProfile) {
      setMail(authProfile.userPrincipalName)
    }
  }, [authProfile]);

  useEffect(() => {
    if (mail && !userProfilePending && userProfile.length <= 0) {
      dispatch(actions.fetchProfileRequest({ userName: mail }));
    }
  }, [mail]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const extractorDetails = useSelector(getExtractorDetailSelector);

  // extractor list
  const extractorList = useSelector(getExtractorListSelector);
  const loadingExtractorList = useSelector(getExtractorListPendingSelector);

  // workflow state
  const workflowState = useSelector(getDataProvisioningWorkflowStateSelector);

  const params: IExtractorListParams = {
    skip: page * rowsPerPage,
    top: rowsPerPage,
    count: true,
    search: searchTerm,
    schema: schemas,
    table: tables,
    treatmentLevel: treatmentLevels,
    accessControl: accessControls,
    sourceName: sourceNames,
    dataStewards: dataStewards,
    // owner: owners,
    spokeName: spokeName,
    orderby: `${orderBy} ${order}`
  };



  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(fetchExtractorListReq(params));
    dispatch(resetWorkflow());
  }, [page, rowsPerPage, searchTerm, orderBy, order]);

  useEffect(() => {
    let data: IDataExtractor[] = extractorList.extractors;
    setDataRows(data);
    setTotalRows(extractorList.count);
  }, [extractorList]);

  useEffect(() => {
    let isDetailExists = false;
    if (extractorDetails) {
      isDetailExists =
        rowDetails.filter((x) => x && x.tableId === extractorDetails!.tableId)
          .length > 0;
      if (!isDetailExists) {
        setRowDetails([...rowDetails, extractorDetails!]);
      }
    }
  }, [extractorDetails, rowDetails]);

  // table related events

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property as keyof IDataExtractor);
    dispatch(fetchExtractorListReq(params));
  };

  const handleClick = (
    event: React.MouseEvent<unknown>,
    extractor: IDataExtractor
  ) => {
    // if an extractor is already selected then close the workflow popup
    if (selectedExtractor) {
      setSelectedExtractor(false);
      dispatch(disableWorkflow());
      dispatch(resetWorkflow());
      navigate("/SearchData");
    }

    // if an extractor is not selected then open the workflow popup
    if (!selectedExtractor) {
      setSelectedExtractor(true);
      dispatch(toggleDataSet(extractor));
      dispatch(enableWorkflow());
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setSearchTerm(e.target.value)
    
  };

  const filterCells: string[] = [
    "schema",
    "table",
    "treatmentLevel",
    "accessControl",
    "sourceName",
    "dataStewards",
    "spokeName"
  ];

  const deleteAllClick = () => {
    setSelected([]);
  };

  const tableToolBar = () => {
    return (
      <EnhancedTableToolbar
        numSelected={selected.length}
        title={"Data Extractors"}
        headCells={headCells}
        filterCells={filterCells}
        deleteAll={deleteAllClick}
        filterResults={filterResults}
        refreshPage={refreshPage}
        currentFilters={filters}
        openToolbar={openFilters}
      />
    );
  };

  const refreshPage = () => {
    let f: string[] = [];
    setSchemas(f);
    params.schema = f;

    setTables(f);
    params.table = f;

    setTreatmentLevels(f);
    params.treatmentLevel = f;

    setAccessControls(f);
    params.accessControl = f;

    setSourceNames(f);
    params.sourceName = f;

    setSpokeName([]);
    params.spokeName = [];

    // setOwners([])
    // params.owner = [];


    setDataStewards([])
    params.dataStewards = [];


    setOrder('asc')
    setOrderBy("description")

    setFilters([]);
    setOpenFilters(false);
    dispatch(fetchExtractorListReq(params));
  };

  const filterResults = (
    event: React.MouseEvent<HTMLButtonElement>,
    filters: string[]
  ) => {
    setSchemas([]);
    setTables([]);
    setTreatmentLevels([]);
    setAccessControls([]);
    setDataStewards([]);
    setSpokeName([]);

    filters.forEach((item) => {
      let filter: string[] = item.split(": ");
      switch (filter[0]) {
        case "schema":
          let s = schemas;
          s.push(filter[1]);
          setSchemas(s);
          break;
        case "table":
          let t = tables;
          t.push(filter[1]);
          setTables(t);
          break;
        case "treatmentLevel":
          let tl = treatmentLevels;
          tl.push(filter[1]);
          setTreatmentLevels(tl);
          break;
        case "accessControl":
          let a = accessControls;
          a.push(filter[1]);
          setAccessControls(a);
          break;
        case "sourceName":
          let c = sourceNames;
          c.push(filter[1]);
          setSourceNames(c);
          break;
        case 'dataStewards':
          let ds = dataStewards;
          ds.push(filter[1]);
          setDataStewards(ds);
          break;
        case 'spokeName':
          let sp = spokeName;
          sp.push(filter[1]);
          setSpokeName(sp);
          break;
        default:
          break;
      }
    });
    setFilters(filters);
    setOpenFilters(true);
    dispatch(fetchExtractorListReq(params));
  };

  const handleSearchDebounce = useMemo(() => {
    return debounce(handleSearch, SEARCH_DEBOUNCE_TIME_MILLIS);
  }, []);

  const searchEl = () => {
    return (
      <TextField
        id="source-search"
        label="Search for data"
        type="search"
        fullWidth={true}
        onChange={handleSearchDebounce}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
    );
  };

  const handleExpand = (row: IDataExtractor) => {
    let isExist = rowDetails.find(
      (x) => x && x.businessMetadata.name === row.tableId
    );

    if (!isExist) {
      // dispatch get set details action
      dispatch(fetchExtractorDetailReq(row.batchCode));
    }
  };

  const handleExtractorView = (row: IDataExtractor,event:React.MouseEvent<HTMLButtonElement>) => {
    const url =`/ViewDataExtractor/${row.batchCode}`
    if(event.ctrlKey){
      window.open(url,'_blank');
    }
    else{
    navigate(url);}
  };

  const tableHeader = () => {
    return (
      <EnhancedTableHead
        headCells={headCells}
        numSelected={selected.length}
        selectable={allSelectable}
        order={order}
        orderBy={orderBy}
        onRequestSort={handleRequestSort}
        rowCount={totalRows}
      />
    );
  };

  useEffect(() => {
    if (!selectable) {
      alert("Close");
    }
  }, [selectable]);

  const checkAccessControl = (row: IDataExtractor) => {
    let accessList: string[] = [];
    let getAccessResult: any = [];

    if (row.accessControl !== null) {
      var splitIDs = row.accessControl.split(";").filter((x) => x !== "");

      splitIDs.forEach(function (value) {
        accessList.push(value);
      });
    }

    if (userProfile.length && accessList) {
      accessList.map((x: string) => {
        return userProfile.map((y) => { return getAccessResult.push(y.adGroupName.toLowerCase() === x.toLowerCase()); })
    })
    }

    return getAccessResult.some(Boolean);
  };

  const filterOwner = (owner: string) => {
    if (owner.toLowerCase() === mail.toLowerCase()) {
      return true
    }
    else return false
  };

  const checkGranteeList = (row: IDataExtractor) => {
    let granteeList: any = [];
    let getGranteeResult: any = [];

    if (row.grantees && row.grantees !== null) {
      var splitIDs = row.grantees.split(",").filter((x) => x !== "");

      splitIDs.forEach(function (value) {
        granteeList.push(value);
      });
    }

    // check if the AD group returned matched the AD group that the user is a part of
    //Override canbe granted to single user or AD Group
    if (userProfile.length > 0 && granteeList.length > 0) {
      granteeList.map((x: string) => {
        if (x.includes('@riotinto.com')) {
          return userProfile.map((y) => { return getGranteeResult.push(y.userName.toLowerCase() === x.toLowerCase()); })
        }
        else {
          return userProfile.map((y) => { return getGranteeResult.push(y.adGroupName.toLowerCase() === x.toLowerCase()); })
        }
      })
    }

    return getGranteeResult.some(Boolean);

  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalRows) : 0;

  const tableRows = () => {
    return (
      <>
        {dataRows?.length > 0 &&
          dataRows
            .slice()
            .sort(getComparator(order, orderBy))
            .map((row: IDataExtractor, index: number) => {
              const isItemSelected =
                workflowState?.selectedDataSet?.tableId === row.tableId;
              const details = rowDetails.find(
                (x) => x && x.businessMetadata.name === row.tableId
              );
              // a data set can be selected only if 
              // 1- No Access Controls
              // 2- If Access Control then you should be a part of it
              // 3- Overrides
              const checkAccess: boolean = checkAccessControl(row)
              const checkGrantee: boolean = checkGranteeList(row)
              const checkOwner = (row.owner !== null ? filterOwner(row.owner) : false) 
              // const checkOwner = filterOwner(row.owner)
              return (
                <Row
                  row={row}
                  key={row.tableId}
                  headCells={headCells}
                  // selectable={selectable}
                  selectable={row.accessControl === "" || row.accessControl === null || checkGrantee || checkAccess || checkOwner || props.isAdmin ? "single" : "na"}
                  canEdit={false}
                  canView={true}
                  canDelete={false}
                  canExpand={true}
                  isItemSelected={isItemSelected}
                  // handleClick={(event) => handleClick(event, row)}
                  handleClick={(event) => row.accessControl === "" || row.accessControl === null || checkGrantee || checkAccess || checkOwner || props.isAdmin ? handleClick(event, row) : ''}
                  handleExpand={handleExpand}
                  handleView={handleExtractorView}
                >
                  {rowDetails.length > 0 && (
                    <Box className="expand-details" sx={{ margin: 1 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={6}>
                          <div>
                            <span className="title">Name : </span>
                            <span>
                              {row.description ?? "None"}
                            </span>
                          </div>
                          <div>
                            <span className="title">Source Name : </span>
                            <span>{row.sourceName ?? "None"}</span>
                          </div>
                          <div>
                            <span className="title">Last Refresh Date: </span>
                            <span>{row.lastRefreshDate ?? "None"}</span>
                          </div>
                        </Grid>
                        <Grid item xs={6}>
                          <div>
                            <span className="title">
                              Security Classification :{" "}
                            </span>
                            <span>
                              {getSecurityClassificationText(
                                row.securityClassification
                              ) ?? "None"}
                            </span>
                          </div>
                          <div>
                            <span className="title">
                              Privacy Classification :{" "}
                            </span>
                            <span>
                              {getPrivacyClassificationText(
                                row.privacyClassification
                              ) ?? "None"}
                            </span>
                          </div>
                          <div>
                            <span className="title">Access Controls : </span>
                            <span>
                              {row.accessControl ??
                                "None"}
                            </span>
                          </div>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Row>
              );
            })}
        {emptyRows > 0 && (
          <TableRow
            style={{
              height: 53 * emptyRows,
            }}
          >
            <TableCell colSpan={12} />
          </TableRow>
        )}
      </>
    );
  };

  const listGrid = () => {
    return (
      <>
        {/* adding skeleton screen on loading */}
        {loadingExtractorList ? (
          <Grid container width="100%">
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
          </Grid>
        ) : (
          <>
            <EnhancedTable
              totalRows={totalRows}
              tableHeader={tableHeader()}
              tableRows={tableRows()}
              pagination={true}
              page={page}
              tableToolBar={tableToolBar()}
              rowsPerPage={rowsPerPage}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
            <DataProvisioningWorkflow />
          </>
        )}
      </>
    );
  };

  return (
    <>
      <SearchListGrid searchEl={searchEl()} children={listGrid()} />
    </>
  );
};

export default SetExplorer;
